// src/Hooks/useKmlState.js

import { useState, useRef } from 'react';

// AMENDED: Added stripHtml helper to clean description after stripping images
const stripHtml = (html) => {
  if (!html) return '';
  const tmp = document.createElement('DIV');
  tmp.innerHTML = html;
  return tmp.textContent || tmp.innerText || '';
};

export const useKmlState = () => {
  const fileInputRef = useRef(null);

  // --- Core UI & Data State ---
  const [viewMode, setViewMode] = useState('DATA_VIEW');
  const [parsedData, setParsedData] = useState([]);
  const [originalFilename, setOriginalFilename] = useState('');
  const [currentFilename, setCurrentFilename] = useState('');
  const [selectedImportIds, setSelectedImportIds] = useState([]);

  // --- Settings & Mappings ---
  const [categories, setCategories] = useState([]);
  const [regions, setRegions] = useState([]);
  const [globalCategory, setGlobalCategory] = useState('');
  const [makePublicDefaultDays, setMakePublicDefaultDays] = useState(0);
  const [makeUppercase, setMakeUppercase] = useState(true);
  const [markerIcons, setMarkerIcons] = useState([]);
  const [customRows, setCustomRows] = useState([]);
  const [selectedCustomIds, setSelectedCustomIds] = useState([]);
  const [rowMappings, setRowMappings] = useState({});
  const [rowSubMappings, setRowSubMappings] = useState({});
  const [rowStaticValues, setRowStaticValues] = useState({});

  // --- Processing & UI State ---
  const [isMinimized, setIsMinimized] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);

  // --- Deferred Deletion State ---
  const [pendingReinstateBatchIds, setPendingReinstateBatchIds] = useState([]);

  // --- Dialogs & Alerts ---
  const [alertState, setAlertState] = useState({
    open: false,
    message: '',
    severity: 'info',
  });
  const [restrictedAlert, setRestrictedAlert] = useState({ open: false });
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showDeleteConfirm2, setShowDeleteConfirm2] = useState(false);
  const [showMoveConfirm, setShowMoveConfirm] = useState(false);
  const [showCloseConfirm, setShowCloseConfirm] = useState(false);
  const [showBatchFilenameDialog, setShowBatchFilenameDialog] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    message: '',
    severity: 'info',
    confirmText: 'Yes',
    cancelText: 'No',
  });

  // --- History ---
  const [importHistory, setImportHistory] = useState([]);
  const [isLoadingHistory, setIsLoadingHistory] = useState(true);
  const [selectedHistoryIds, setSelectedHistoryIds] = useState([]);

  // --- Derived Flags ---
  const hasData = parsedData.length > 0;
  const hasUnimported = parsedData.some((row) => !row.isImported);
  const isHistoryView = viewMode === 'HISTORY_VIEW';

  const canSave = hasUnimported && viewMode === 'DATA_VIEW' && !isProcessing;

  const canDeleteData =
    (viewMode === 'SETTINGS_VIEW' && selectedCustomIds.length > 0 && !isProcessing) ||
    (viewMode === 'DATA_VIEW' && selectedImportIds.length > 0 && !isProcessing) ||
    (viewMode === 'HISTORY_VIEW' && selectedHistoryIds.length > 0 && !isProcessing);

  const canUndoHistory = isHistoryView && selectedHistoryIds.length > 0 && !isProcessing;

  // --- Pure UI Handlers ---
  const handleNavFirst = () => setViewMode('DATA_VIEW');
  const handleNavLast = () => setViewMode('HISTORY_VIEW');
  const handleNavBack = () => {
    if (viewMode === 'HISTORY_VIEW') setViewMode('SETTINGS_VIEW');
    else if (viewMode === 'SETTINGS_VIEW') setViewMode('DATA_VIEW');
  };
  const handleNavForward = () => {
    if (viewMode === 'DATA_VIEW') setViewMode('SETTINGS_VIEW');
    else if (viewMode === 'SETTINGS_VIEW') setViewMode('HISTORY_VIEW');
  };

  const handleAddCustomRow = () => {
    setCustomRows((prev) => [
      ...prev,
      { id: `custom_${Date.now()}`, name: '-', isCustom: true },
    ]);
  };

  const handleCustomRowNameChange = (id, newName) => {
    setCustomRows((prev) =>
      prev.map((row) => (row.id === id ? { ...row, name: newName } : row)),
    );
  };

  const handleAcknowledgeRestricted = () => {
    setRestrictedAlert({ open: false });
    setParsedData((prev) =>
      prev.map((d) => {
        const noImageDesc = (d.description || '').replace(/<img[^>]*>/gi, '');
        return {
          ...d,
          imageCount: 0,
          imageUrls: [],
          description: noImageDesc,
          cleanDescription: stripHtml(noImageDesc),
          hasRichText: /<[a-z][\s\S]*>/i.test(noImageDesc),
        };
      }),
    );
  };

  return {
    refs: { fileInputRef },
    state: {
      viewMode,
      parsedData,
      originalFilename,
      currentFilename,
      selectedImportIds,
      categories,
      regions,
      globalCategory,
      makePublicDefaultDays,
      makeUppercase,
      markerIcons,
      customRows,
      selectedCustomIds,
      rowMappings,
      rowSubMappings,
      rowStaticValues,
      isMinimized,
      isProcessing,
      alertState,
      restrictedAlert,
      showDeleteConfirm,
      showDeleteConfirm2,
      showMoveConfirm,
      showCloseConfirm,
      showBatchFilenameDialog,
      confirmDialog,
      importHistory,
      isLoadingHistory,
      selectedHistoryIds,
      pendingReinstateBatchIds,
    },
    setters: {
      setViewMode,
      setParsedData,
      setOriginalFilename,
      setCurrentFilename,
      setSelectedImportIds,
      setCategories,
      setRegions,
      setGlobalCategory,
      setMakePublicDefaultDays,
      setMakeUppercase,
      setMarkerIcons,
      setCustomRows,
      setSelectedCustomIds,
      setRowMappings,
      setRowSubMappings,
      setRowStaticValues,
      setIsMinimized,
      setIsProcessing,
      setAlertState,
      setRestrictedAlert,
      setShowDeleteConfirm,
      setShowDeleteConfirm2,
      setShowMoveConfirm,
      setShowCloseConfirm,
      setShowBatchFilenameDialog,
      setConfirmDialog,
      setImportHistory,
      setIsLoadingHistory,
      setSelectedHistoryIds,
      setPendingReinstateBatchIds,
    },
    flags: { hasData, isHistoryView, canSave, canDeleteData, canUndoHistory },
    uiActions: {
      handleNavFirst,
      handleNavLast,
      handleNavBack,
      handleNavForward,
      handleAddCustomRow,
      handleCustomRowNameChange,
      handleAcknowledgeRestricted,
    },
  };
};
